import React from 'react';
import { Link } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTheme } from '../context/ThemeContext';
import { 
  FaArrowLeft as FaArrowLeftBase, 
  FaYoutube as FaYoutubeBase 
} from 'react-icons/fa';

// Fix for TS2786
const FaArrowLeft = FaArrowLeftBase as React.ElementType;
const FaYoutube = FaYoutubeBase as React.ElementType;

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const YouTubeSentimentAnalyzer: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const data = {
    labels: ['Positive', 'Neutral', 'Negative'],
    datasets: [{
      label: 'Comment Distribution',
      data: [65, 20, 15],
      backgroundColor: ['#10b981', '#9ca3af', '#ef4444'],
      borderRadius: 8,
    }]
  };

  const sampleComments = [
    { text: "This tutorial saved my life! The explanation of hooks was perfect.", sentiment: 'Positive', score: 0.98 },
    { text: "Audio quality is a bit low, hard to hear the code explanation.", sentiment: 'Negative', score: 0.23 },
    { text: "Can you do a video on Redux next?", sentiment: 'Neutral', score: 0.51 },
  ];

  return (
    <div className="min-h-screen bg-secondary pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/portfolio" className="inline-flex items-center text-secondary hover:text-accent mb-8 transition-colors">
          <FaArrowLeft className="mr-2" /> Back to Portfolio
        </Link>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-red-600 rounded-full text-white">
            <FaYoutube size={32} />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-primary">Video Sentiment Analyzer</h1>
            <p className="text-secondary">NLP pipeline to gauge audience reaction from YouTube comment sections.</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          {/* Chart Section */}
          <div className="bg-primary p-6 rounded-xl shadow-lg border border-primary">
            <h3 className="text-lg font-bold text-primary mb-6">Sentiment Overview</h3>
            <Bar 
              data={data} 
              options={{ 
                responsive: true, 
                scales: { 
                  y: { grid: { color: isDark ? '#374151' : '#e5e7eb' } },
                  x: { grid: { display: false } }
                } 
              }} 
            />
          </div>

          {/* Sample Analysis Section */}
          <div className="bg-primary p-6 rounded-xl shadow-lg border border-primary">
            <h3 className="text-lg font-bold text-primary mb-6">Live Analysis Sample</h3>
            <div className="space-y-4">
              {sampleComments.map((c, i) => (
                <div key={i} className="p-4 bg-secondary rounded-lg border border-primary">
                  <div className="flex justify-between items-start mb-2">
                    <span className={`px-2 py-1 text-xs rounded font-bold ${
                      c.sentiment === 'Positive' ? 'bg-green-100 text-green-800' :
                      c.sentiment === 'Negative' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {c.sentiment} ({c.score})
                    </span>
                  </div>
                  <p className="text-primary text-sm">"{c.text}"</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-primary p-8 rounded-xl border border-primary">
          <h2 className="text-2xl font-bold text-primary mb-4">Under the Hood</h2>
          <p className="text-secondary mb-4">
            This application uses the YouTube Data API v3 to fetch comments from a provided video ID. 
            The text is cleaned (removing emojis, links, and stop words) and analyzed by Google's Gemini 1.5 Flash to classify sentiment and extract themes at scale.
          </p>
          <div className="flex gap-4 mt-6">
            <span className="px-3 py-1 bg-secondary border border-primary rounded text-sm text-primary">Python</span>
            <span className="px-3 py-1 bg-secondary border border-primary rounded text-sm text-primary">Gemini 1.5 Flash</span>
            <span className="px-3 py-1 bg-secondary border border-primary rounded text-sm text-primary">YouTube Data API</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubeSentimentAnalyzer;